import { GroupedTypeOption, group_promotion_type_options, promotion_type_layout } from "./promotion_type_layout"

export const DEFAULT_PROMOTION_TYPES = [
  { value: "TPR", label: "TPR (Temporary Price Reduction)" },
  { value: "Scan", label: "Scan" },
  { value: "MCB", label: "MCB (Manufacturer Chargeback)" },
  { value: "OI", label: "OI (Off Invoice)" },
  { value: "Multi-Buy", label: "Multi-Buy (e.g., 2 for $6)" },
  { value: "BOGO", label: "BOGO (Buy One Get One)" },
  { value: "Slotting", label: "Slotting" },
  { value: "Display", label: "Display" },
  { value: "EDLP", label: "EDLP (Every Day Low Price)" },
  { value: "Coupon", label: "Coupon" },
  { value: "Non-Working Accrual", label: "Non-Working Accrual" },
  { value: "Program Fee", label: "Program Fee" },
  { value: "Other", label: "Other" },
]

// Legacy export for backward compatibility
export const PROMOTION_TYPES = DEFAULT_PROMOTION_TYPES

export function createPromotionTypeOptions(existingTypes: string[]): GroupedTypeOption[] {
  const existingOptions = existingTypes
    .filter(type => type && type.trim() !== '')
    .map(type => ({
      value: type,
      label: type
    }))
  
  // Combine existing types with default types, removing duplicates
  const allOptions = [...existingOptions, ...DEFAULT_PROMOTION_TYPES]
  const uniqueOptions = allOptions.filter((option, index, self) => 
    index === self.findIndex(o => o.value === option.value)
  )
  
  // Grouped by category (Price promotion, Scan or chargeback, …) — see promotion_type_layout.
  return group_promotion_type_options(uniqueOptions)
}

/** Which discount inputs a type shows. Derived from `promotion_type_layout`, the single rule. */
export function getDiscountFieldsForType(promotionType: string) {
  const layout = promotion_type_layout(promotionType)
  return {
    showAmount: layout.deal_fields === "discount",
    showPercentage: layout.deal_fields === "discount",
    showMultibuy: layout.deal_fields === "multibuy",
    defaultToPercentage: layout.deal_fields === "discount",
  }
}

export const PROMOTION_COLORS = {
  'TPR': {
    background: '#eff6ff', // blue-50
    text: '#1e40af',        // blue-800
    selectedBackground: '#3b82f6', // blue-500
    selectedText: '#ffffff',
    border: '#3b82f6' // blue-500
  },
  'Scan': {
    background: '#f0fdf4', // green-50
    text: '#15803d',        // green-800
    selectedBackground: '#22c55e', // green-500
    selectedText: '#ffffff',
    border: '#22c55e' // green-500
  },
  'MCB': {
    background: '#faf5ff', // purple-50
    text: '#581c87',        // purple-800
    selectedBackground: '#a855f7', // purple-500
    selectedText: '#ffffff',
    border: '#a855f7' // purple-500
  },
  'OI': {
    background: '#fff7ed', // orange-50
    text: '#9a3412',        // orange-800
    selectedBackground: '#f97316', // orange-500
    selectedText: '#ffffff',
    border: '#f97316' // orange-500
  },
  'Multi-Buy': {
    background: '#fdf2f8', // pink-50
    text: '#831843',        // pink-800
    selectedBackground: '#ec4899', // pink-500
    selectedText: '#ffffff',
    border: '#ec4899' // pink-500
  },
  'BOGO': {
    background: '#faf5ff', // violet-50
    text: '#581c87',        // violet-800
    selectedBackground: '#8b5cf6', // violet-500
    selectedText: '#ffffff',
    border: '#8b5cf6' // violet-500
  },
  'Slotting': {
    background: '#ecfdf5', // emerald-50
    text: '#064e3b',        // emerald-800
    selectedBackground: '#10b981', // emerald-500
    selectedText: '#ffffff',
    border: '#10b981' // emerald-500
  },
  'Display': {
    background: '#faf5ff', // violet-50
    text: '#581c87',        // violet-800
    selectedBackground: '#8b5cf6', // violet-500
    selectedText: '#ffffff',
    border: '#8b5cf6' // violet-500
  },
  'EDLP': {
    background: '#f0fdf4', // green-50
    text: '#15803d',        // green-800
    selectedBackground: '#22c55e', // green-500
    selectedText: '#ffffff',
    border: '#22c55e' // green-500
  },
  'Coupon': {
    background: '#fef3c7', // yellow-50
    text: '#92400e',        // yellow-800
    selectedBackground: '#eab308', // yellow-500
    selectedText: '#ffffff',
    border: '#eab308' // yellow-500
  },
  'Non-Working Accrual': {
    background: '#fce7f3', // pink-50
    text: '#be185d',        // pink-800
    selectedBackground: '#ec4899', // pink-500
    selectedText: '#ffffff',
    border: '#ec4899' // pink-500
  },
  'Program Fee': {
    background: '#e0e7ff', // indigo-50
    text: '#3730a3',        // indigo-800
    selectedBackground: '#6366f1', // indigo-500
    selectedText: '#ffffff',
    border: '#6366f1' // indigo-500
  },
  'Other': {
    background: '#f9fafb', // gray-50
    text: '#1f2937',        // gray-800
    selectedBackground: '#6b7280', // gray-500
    selectedText: '#ffffff',
    border: '#6b7280' // gray-500
  },
  'default': {
    background: '#f9fafb', // gray-50
    text: '#1f2937',        // gray-800
    selectedBackground: '#6b7280', // gray-500
    selectedText: '#ffffff',
    border: '#6b7280' // gray-500
  }
}

// Color palette for unmapped promotion types
const GENERATED_COLOR_PALETTE = [
  { background: '#fef2f2', text: '#991b1b', selectedBackground: '#ef4444', selectedText: '#ffffff', border: '#ef4444' }, // red
  { background: '#fff1f2', text: '#9f1239', selectedBackground: '#f43f5e', selectedText: '#ffffff', border: '#f43f5e' }, // rose
  { background: '#fff7ed', text: '#9a3412', selectedBackground: '#f97316', selectedText: '#ffffff', border: '#f97316' }, // orange
  { background: '#fffbeb', text: '#92400e', selectedBackground: '#f59e0b', selectedText: '#ffffff', border: '#f59e0b' }, // amber
  { background: '#f0fdfa', text: '#134e4a', selectedBackground: '#14b8a6', selectedText: '#ffffff', border: '#14b8a6' }, // teal
  { background: '#ecfeff', text: '#164e63', selectedBackground: '#06b6d4', selectedText: '#ffffff', border: '#06b6d4' }, // cyan
  { background: '#eff6ff', text: '#1e40af', selectedBackground: '#3b82f6', selectedText: '#ffffff', border: '#3b82f6' }, // blue
  { background: '#eef2ff', text: '#3730a3', selectedBackground: '#6366f1', selectedText: '#ffffff', border: '#6366f1' }, // indigo
  { background: '#f5f3ff', text: '#5b21b6', selectedBackground: '#8b5cf6', selectedText: '#ffffff', border: '#8b5cf6' }, // violet
  { background: '#faf5ff', text: '#6b21a8', selectedBackground: '#a855f7', selectedText: '#ffffff', border: '#a855f7' }, // purple
  { background: '#fdf4ff', text: '#86198f', selectedBackground: '#c084fc', selectedText: '#ffffff', border: '#c084fc' }, // fuchsia
  { background: '#fdf2f8', text: '#831843', selectedBackground: '#ec4899', selectedText: '#ffffff', border: '#ec4899' }, // pink
  { background: '#f0fdf4', text: '#166534', selectedBackground: '#22c55e', selectedText: '#ffffff', border: '#22c55e' }, // green
  { background: '#ecfdf5', text: '#14532d', selectedBackground: '#10b981', selectedText: '#ffffff', border: '#10b981' }, // emerald
  { background: '#fef3c7', text: '#78350f', selectedBackground: '#eab308', selectedText: '#ffffff', border: '#eab308' }, // yellow
  { background: '#fefce8', text: '#713f12', selectedBackground: '#84cc16', selectedText: '#ffffff', border: '#84cc16' }, // lime
  { background: '#f7fee7', text: '#365314', selectedBackground: '#65a30d', selectedText: '#ffffff', border: '#65a30d' }, // green (darker)
  { background: '#f0f9ff', text: '#0c4a6e', selectedBackground: '#0ea5e9', selectedText: '#ffffff', border: '#0ea5e9' }, // sky
  { background: '#f8fafc', text: '#334155', selectedBackground: '#64748b', selectedText: '#ffffff', border: '#64748b' }, // slate
  { background: '#f1f5f9', text: '#475569', selectedBackground: '#475569', selectedText: '#ffffff', border: '#475569' }, // slate (darker)
]

// Simple hash function to deterministically assign colors
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

// Get colors for a promotion type, generating them if not explicitly mapped
export function getPromotionTypeColors(promotionType?: string): { background: string; text: string; selectedBackground: string; selectedText: string; border: string } {
  if (!promotionType) {
    return PROMOTION_COLORS['Other']
  }
  
  // Check if there's an explicit mapping
  const explicitColors = PROMOTION_COLORS[promotionType as keyof typeof PROMOTION_COLORS]
  if (explicitColors && explicitColors.selectedBackground) {
    return explicitColors
  }
  
  // Generate color based on hash of promotion type name
  const hash = hashString(promotionType)
  const colorIndex = hash % GENERATED_COLOR_PALETTE.length
  return GENERATED_COLOR_PALETTE[colorIndex]
}

// Generic function to get colors for any filter label (retailers, distributors, etc.)
export function getFilterLabelColors(label: string): { background: string; text: string; selectedBackground: string; selectedText: string; border: string } {
  // Generate color based on hash of label name
  const hash = hashString(label)
  const colorIndex = hash % GENERATED_COLOR_PALETTE.length
  return GENERATED_COLOR_PALETTE[colorIndex]
}
